import { formatMonto } from '../utils/formatters.js';

/**
 * Pure payment-derivation policy (Unit S5b, design.md AD13/AD14/AD18, spec
 * `payroll-payments`). Client-only — deliberately NO `api/_lib` twin (AD14):
 * the never-exceed-neto invariant is enforced exactly ONCE, in SQL
 * (`registrar_pago`'s `for update` + re-read, migration 29's
 * `docs/supabase/29-payroll-pagos.sql`). This module only DERIVES a display
 * state from data already read, comparing in integer centavos so no JS float
 * can flicker `pagado` on or off (spec "Rounding never flickers the state").
 *
 * No DOM, no I/O, no `Date`/`Intl` for dates — `formatMonto` is the one
 * exception, already the precedent `liquidacion-comprobante-policy.js` sets
 * for a "pure policy" file (money formatting, not calendar math).
 */

/**
 * Convierte un monto a centavos ENTEROS. SILENT ZERO (AD14): un valor no
 * finito NUNCA se coerciona a `0` — el llamador debe tratar esto como un
 * error de datos, nunca como "sin pagos".
 * @param {number} v
 * @returns {number}
 */
export function aCentavos(v) {
    if (typeof v !== 'number' || !Number.isFinite(v)) throw new TypeError(`aCentavos: valor no finito (${v})`);
    return Math.round(v * 100);
}

/**
 * Suma en centavos de los pagos ACTIVOS (`anulado_at` ausente/`null`) de una
 * lista — un pago anulado nunca aporta al estado derivado (spec "Derived
 * Payment State" habla siempre de `sum(active)`).
 * @param {Array<{ monto: number, anulado_at?: string|null }>} pagos
 * @returns {number}
 */
function sumaActivosCentavos(pagos) {
    let total = 0;
    for (const p of pagos ?? []) {
        if (p.anulado_at) continue;
        total += aCentavos(Number(p.monto));
    }
    return total;
}

/**
 * Estado derivado de una liquidación (spec "Derived Payment State" — tabla
 * literal). `neto <= 0` es SIEMPRE "pagado" sin importar la suma activa
 * (columna dedicada de esa misma tabla).
 * @param {{ neto: number, pagos: Array<{monto:number, anulado_at?:string|null}> }} params
 * @returns {{ estado: 'pendiente'|'parcial'|'pagado', pagadoCentavos: number, saldoCentavos: number, pill: { texto: string, tono: 'pendiente'|'parcial'|'pagado' } }}
 */
export function estadoPago({ neto, pagos } = {}) {
    const netoCentavos = aCentavos(Number(neto));
    const pagadoCentavos = sumaActivosCentavos(pagos);
    const saldoCentavos = Math.max(0, netoCentavos - pagadoCentavos);

    if (netoCentavos <= 0 || pagadoCentavos >= netoCentavos) {
        return { estado: 'pagado', pagadoCentavos, saldoCentavos, pill: { texto: 'Pagado', tono: 'pagado' } };
    }
    if (pagadoCentavos === 0) {
        return { estado: 'pendiente', pagadoCentavos, saldoCentavos, pill: { texto: 'Pendiente', tono: 'pendiente' } };
    }
    return {
        estado: 'parcial',
        pagadoCentavos,
        saldoCentavos,
        pill: { texto: `Parcial · ${formatMonto(pagadoCentavos / 100)} de ${formatMonto(netoCentavos / 100)}`, tono: 'parcial' },
    };
}

/**
 * Monto a precargar en el form de alta (spec "Register Payment Drawer" —
 * "monto pre-filled with the saldo"): `neto - pagado`, nunca negativo (el
 * RPC lo impide en SQL — AD13 — pero esta función no confía en eso).
 * @param {{ neto: number, pagos: Array<object> }} params
 * @returns {number} 2 decimales
 */
export function saldoSugerido({ neto, pagos } = {}) {
    return estadoPago({ neto, pagos }).saldoCentavos / 100;
}

/**
 * S5b (design.md AD18) — texto literal "Ya se pagaron $X de una liquidación
 * anulada de este período (...)"; COMPARTIDO por la fila
 * (`LiquidacionTableroView.js`) y el `ConfirmDialog` de Liquidar
 * (`js/admin.js`) — antes duplicado en ambos lados (review-a57c3fe2ce2f2297,
 * finding 4). Puro: `periodosFormateados` ya viene con cada rango formateado
 * `dd/mm a dd/mm` por el CALLER — cada uno con su propio formateador de
 * fecha string-only (`formatDiaMes`/`formatDiaMesSql`), que siguen sin
 * compartirse (mismo criterio deliberado que `AdelantoDrawerView.js#formatFechaSql`).
 * @param {{ total: number, periodosFormateados: string[] }} params
 * @returns {string}
 */
export function textoAvisoPagoPrevio({ total, periodosFormateados } = {}) {
    return `Ya se pagaron ${formatMonto(total)} de una liquidación anulada de este período (${(periodosFormateados ?? []).join(', ')}).`;
}

/**
 * AD18 — "ya se pagó bajo una liquidación anulada de este período". Puro:
 * `liquidaciones` es el historial YA cargado (confirmadas Y anuladas),
 * `pagos` es la ÚNICA lectura batcheada que también alimenta la pill de
 * "Liquidada" (el caller amplía su `liquidacion_id in (...)` para incluir
 * estas anuladas — nunca una segunda lectura). Predicado de overlap
 * IDÉNTICO a AD4: nunca containment.
 *
 * Cuenta ÚNICAMENTE: `estado === 'anulada'` && solapa `periodo` &&
 * `pago.anulado_at === null`. Un empleado sin ningún pago así está AUSENTE
 * del mapa, nunca un `0` (spec: "an employee with no such payment... never a
 * zero entry" — reglas de negocio ausentes se leen como ausencia, no como
 * cero).
 * @param {Array<{ id: string, empleado_id: string, estado: string, periodo_desde: string, periodo_hasta: string }>} liquidaciones
 * @param {Array<{ liquidacion_id: string, monto: number, anulado_at?: string|null }>} pagos
 * @param {{ desde: string, hasta: string }} periodo
 * @returns {Map<string, { totalCentavos: number, total: number, periodos: Array<{desde:string,hasta:string}> }>}
 */
export function pagosPreviosDeAnuladas(liquidaciones, pagos, { desde, hasta } = {}) {
    const anuladasSolapadas = new Map();
    for (const l of liquidaciones ?? []) {
        if (l.estado !== 'anulada') continue;
        if (!(l.periodo_desde <= hasta && l.periodo_hasta >= desde)) continue;
        anuladasSolapadas.set(l.id, l);
    }

    const porEmpleado = new Map();
    for (const p of pagos ?? []) {
        if (p.anulado_at) continue;
        const liquidacion = anuladasSolapadas.get(p.liquidacion_id);
        if (!liquidacion) continue;

        let acc = porEmpleado.get(liquidacion.empleado_id);
        if (!acc) {
            acc = { totalCentavos: 0, liquidacionesVistas: new Set(), periodos: [] };
            porEmpleado.set(liquidacion.empleado_id, acc);
        }
        acc.totalCentavos += aCentavos(Number(p.monto));
        // Una liquidación anulada aporta su período UNA sola vez, aunque
        // tenga varios pagos activos — "dos anuladas... listing both
        // periodos" habla de LIQUIDACIONES distintas, no de pagos distintos.
        if (!acc.liquidacionesVistas.has(liquidacion.id)) {
            acc.liquidacionesVistas.add(liquidacion.id);
            acc.periodos.push({ desde: liquidacion.periodo_desde, hasta: liquidacion.periodo_hasta });
        }
    }

    const resultado = new Map();
    for (const [empleadoId, acc] of porEmpleado) {
        resultado.set(empleadoId, { totalCentavos: acc.totalCentavos, total: acc.totalCentavos / 100, periodos: acc.periodos });
    }
    return resultado;
}
